/**
 * SimulationConfig Component
 * Compact, collapsible configuration panel for simulation settings
 */

import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Board size options
const BOARD_SIZES = [
    { label: '5x5', rows: 5, cols: 5 },
    { label: '8x8', rows: 8, cols: 8 },
    { label: '10x10', rows: 10, cols: 10 },
];

// Dice configuration options
const DICE_SIDES_OPTIONS = [4, 6, 8, 10, 12, 20];
const DICE_COUNT_OPTIONS = [1, 2];

/**
 * SimulationConfig - Compact configuration panel
 */
export const SimulationConfig = ({
    boardSize,
    onBoardSizeChange,
    startPosition,
    onStartPositionChange,
    maxPosition,
    diceCount,
    onDiceCountChange,
    diceSides,
    onDiceSidesChange,
    showBoardSize = true,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const boardSizeIndex = BOARD_SIZES.findIndex(s => s.rows === boardSize.rows);
    const isCustomConfig = startPosition > 1 || diceCount !== 1 || diceSides !== 6;

    return (
        <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
            {/* Header - always visible */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
                <div className="flex items-center gap-2">
                    {isExpanded ? (
                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                    ) : (
                        <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                    )}
                    <span className="text-sm font-medium text-gray-700">Configuration</span>
                </div>

                {/* Summary badges */}
                <div className="flex items-center gap-2 text-xs">
                    {showBoardSize && (
                        <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                            {boardSize.rows}x{boardSize.cols}
                        </span>
                    )}
                    <span className={`px-2 py-0.5 rounded ${isCustomConfig ? 'bg-badger-purple text-white' : 'bg-gray-200 text-gray-700'}`}>
                        {diceCount}d{diceSides}
                    </span>
                    {startPosition > 1 && (
                        <span className="bg-badger-purple text-white px-2 py-0.5 rounded">
                            Start: {startPosition}
                        </span>
                    )}
                </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
                <div className="p-3 border-t border-gray-200 bg-white">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {/* Board Size */}
                        {showBoardSize && (
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Board</label>
                                <select
                                    value={boardSizeIndex}
                                    onChange={(e) => onBoardSizeChange(parseInt(e.target.value))}
                                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-badger-purple"
                                >
                                    {BOARD_SIZES.map((size, index) => (
                                        <option key={size.label} value={index}>{size.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Dice Count */}
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Dice</label>
                            <select
                                value={diceCount}
                                onChange={(e) => onDiceCountChange(parseInt(e.target.value))}
                                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-badger-purple"
                            >
                                {DICE_COUNT_OPTIONS.map(count => (
                                    <option key={count} value={count}>{count}</option>
                                ))}
                            </select>
                        </div>

                        {/* Dice Sides */}
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Sides</label>
                            <select
                                value={diceSides}
                                onChange={(e) => onDiceSidesChange(parseInt(e.target.value))}
                                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-badger-purple"
                            >
                                {DICE_SIDES_OPTIONS.map(sides => (
                                    <option key={sides} value={sides}>d{sides}</option>
                                ))}
                            </select>
                        </div>

                        {/* Start Position */}
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Start</label>
                            <input
                                type="number"
                                min={1}
                                max={maxPosition - 1}
                                value={startPosition}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    if (val >= 1 && val < maxPosition) {
                                        onStartPositionChange(val);
                                    }
                                }}
                                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-badger-purple"
                            />
                        </div>
                    </div>

                    {/* Start position slider for finer control */}
                    {maxPosition > 25 && (
                        <div className="mt-3">
                            <input
                                type="range"
                                min={1}
                                max={maxPosition - 1}
                                value={startPosition}
                                onChange={(e) => onStartPositionChange(parseInt(e.target.value, 10))}
                                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-badger-purple"
                            />
                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                <span>1</span>
                                <span>{Math.round((startPosition / maxPosition) * 100)}% through</span>
                                <span>{maxPosition - 1}</span>
                            </div>
                        </div>
                    )}

                    {/* Quick info */}
                    <p className="text-xs text-gray-500 mt-2">
                        Roll range: {diceCount}-{diceCount * diceSides}
                        {startPosition > 1 && ` | Starting ${Math.round((startPosition / maxPosition) * 100)}% through board`}
                    </p>
                </div>
            )}
        </div>
    );
};

export default SimulationConfig;
